// CRM → Bridge45 mapping. Pure functions, mirrored by api/src/services/crm-map.ts.

const RANKS = {
  Universe: 0,
  Researching: 1,
  Contacted: 2,
  Engaged: 3,
  Discovery: 4,
  Opportunity: 5,
  Parked: -1,
  Rejected: -1,
};

function statusRank(key) {
  if (!key) return -1;
  return RANKS[key] ?? -1;
}

const DEVELOPER_HINT = /developer|real.?estate|property|asset|portfolio|fund|investment|family.?office/i;
const UAE_HINT = /uae|united arab|dubai|abu dhabi|sharjah/i;

export function str(v) {
  return v === null || v === undefined ? '' : String(v).trim();
}

export function isoDate(v) {
  const s = str(v);
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

export function mapSegment(industry) {
  const raw = str(industry);
  if (!raw) return 'Other';
  if (/family.?office/i.test(raw)) return 'Family office';
  if (/fund/i.test(raw)) return 'Fund / investment firm';
  if (/portfolio/i.test(raw)) return 'Portfolio owner';
  if (/asset.?owner|owner/i.test(raw)) return 'Asset owner';
  if (/developer|real.?estate|property/i.test(raw)) return 'Developer';
  if (/bank|regulat/i.test(raw)) return 'Regulated institution';
  if (/sponsor|operator/i.test(raw)) return 'Sponsor / operator';
  return 'Other';
}

export function statusFloorFromFacts(facts, crmStatus) {
  const status = str(crmStatus).toLowerCase();
  if (status === 'passed') return 'Parked';
  const f = facts || {};
  // Activity facts only — CRM lifecycle labels are not commercial evidence.
  if (f.meetingCompleted) return 'Discovery';
  if (f.engaged || f.meetingBooked) return 'Engaged';
  if (f.outreach) return 'Contacted';
  return 'Universe';
}

export function raiseStatusFloor(current, floor) {
  if (floor === 'Parked' || floor === 'Rejected') return current && statusRank(current) >= 0 ? current : floor;
  const curRank = statusRank(current);
  const floorRank = statusRank(floor);
  if (curRank < 0) return current || floor;
  return floorRank > curRank ? floor : (current || floor);
}

export function classifyAccount(input = {}) {
  const geo = `${input.country || ''} ${input.city || ''}`;
  const industry = `${input.segment || ''} ${input.industry || ''}`;
  const status = str(input.crmStatus).toLowerCase();
  const scoreNum = Number(input.crmAccountScore) || 0;
  const facts = input.facts || { outreach: false, engaged: false, meetingBooked: false, meetingCompleted: false };

  let score = 0;
  const reasons = [];
  if (UAE_HINT.test(geo) || !str(input.country)) {
    score += 3;
    reasons.push('UAE/Dubai geo');
  }
  if (DEVELOPER_HINT.test(industry)) {
    score += 4;
    reasons.push('developer/asset fit');
  }
  if (scoreNum >= 60) {
    score += 3;
    reasons.push(`CRM score ${scoreNum}`);
  } else if (scoreNum >= 40) {
    score += 1;
    reasons.push(`CRM score ${scoreNum}`);
  }
  if (status === 'engaged' || facts.engaged || facts.meetingBooked) {
    score += 3;
    reasons.push('warm path / engagement');
  }
  if (input.decisionMakerKnown) {
    score += 2;
    reasons.push('decision-maker identified');
  }

  let proposedTier = 'Universe';
  if (score >= 8) proposedTier = 'Tier A';
  else if (score >= 4) proposedTier = 'Qualified Target';

  return {
    proposedTier,
    why: reasons.length ? reasons.join(' + ') : 'insufficient CRM signal',
    score,
  };
}

export function factsFromActivities(engagements = [], meetings = [], accountId) {
  const forAccount = (row) => str(row.accountId) === accountId || str(row.account_id) === accountId;
  const eng = engagements.filter(forAccount);
  const meet = meetings.filter(forAccount);
  const types = new Set(eng.map((e) => str(e.type).toLowerCase()));
  const meetingStatus = meet.map((m) => str(m.status).toLowerCase());
  return {
    outreach: types.has('email') || types.has('call'),
    engaged: types.has('meeting') || meetingStatus.some((s) => s && s !== 'cancelled'),
    meetingBooked: meet.some((m) => {
      const s = str(m.status).toLowerCase();
      return s === 'scheduled' || s === 'confirmed' || s === 'booked';
    }),
    meetingCompleted: meet.some((m) => {
      const s = str(m.status).toLowerCase();
      return s === 'completed' || s === 'done' || s === 'held';
    }),
  };
}

export const BRIDGE_OWNED_FIELDS = [
  'tier',
  'whyBlocks',
  'evidenceLevel',
  'd001Candidate',
  'primaryD001',
  'backupD001',
  'scores',
  'problemConfirmed',
  'completionCondition',
  'parkedReason',
  'rejectedReason',
  'tierReason',
  'waitingExternal',
  'waitingUntil',
  'waitingFor',
  'waitingSince',
  'noResponseAt',
  'noResponseFlag',
];

export function mergeCrmAccount(existing, incoming) {
  const next = { ...(existing || {}), ...incoming };
  if (existing) {
    for (const field of BRIDGE_OWNED_FIELDS) {
      if (existing[field] !== undefined && existing[field] !== '') {
        next[field] = existing[field];
      }
    }
    if (existing.nextAction && incoming.sourceUpdatedAt) {
      const existingTouch = isoDate(existing.updatedAt || existing.nextActionDue);
      const sourceTouch = isoDate(incoming.sourceUpdatedAt);
      if (existingTouch && sourceTouch && existingTouch > sourceTouch) {
        next.nextAction = existing.nextAction;
        next.nextActionDue = existing.nextActionDue;
      }
    }
    next.status = raiseStatusFloor(str(existing.status), str(incoming.status) || 'Universe');
    next.id = existing.id;
  }
  return next;
}

export function crmWritebackForAccept(move) {
  if (!move || !['account', 'opportunity'].includes(move.linkedKind) || !move.linkedId) return null;
  return {
    type: 'accept',
    linkedKind: move.linkedKind,
    linkedId: move.linkedId,
    text: move.text || '',
    dueDate: move.dueDate || '',
  };
}

export function crmWritebackForCloseout(move) {
  if (!move || !['account', 'opportunity'].includes(move.linkedKind) || !move.linkedId) return null;
  return {
    type: 'closeout',
    linkedKind: move.linkedKind,
    linkedId: move.linkedId,
    text: move.nextActionText || move.waitingFor || move.text || '',
    dueDate: move.waitingUntil || move.dueDate || '',
    summary: move.outcomeNote || move.completionResult || move.outcome || move.text || '',
  };
}
